/**
 * 尾程报价管理前端接口
 */

import { request } from '@/utils/http'
import { type BatchDeleteRequest, type PageResponse } from '@/api/types/common'
import {
  type InitiateConsultationRequest, type InqQuotPriceChargingItemVO,
  type QuotationBaseModel, type QuotationChargingItemVO,
  type QuotationPageRequest,
  type QuotationSaveRequest
} from './quotation-base'

/**
 * 尾程报价模型（继承基础报价模型）
 */
export interface QuotationLastMileModel extends QuotationBaseModel {
}

/**
 * 尾程报价分页查询请求参数
 */
export interface QuotationLastMilePageRequest extends QuotationPageRequest {
}

/**
 * 尾程报价保存请求参数
 */
export interface QuotationLastMileSaveRequest extends QuotationSaveRequest {
}

/**
 * 分页查询尾程报价列表
 */
export const pageQuotationLastMile = (params: QuotationLastMilePageRequest) => {
  return request.post<PageResponse<QuotationLastMileModel>>('/price/quotation/last-mile/page', params)
}

/**
 * 根据ID获取尾程报价详情
 */
export const getQuotationLastMileById = (id: string) => {
  return request.post<QuotationLastMileModel>('/price/quotation/last-mile/get', { id })
}

/**
 * 根据询价编号获取尾程报价详情
 */
export const getQuotationLastMileByInquiryNo = (inquiryNo: string) => {
  return request.post<QuotationLastMileModel>('/price/quotation/last-mile/get', { inquiryNo })
}

/**
 * 新增尾程报价
 */
export const saveQuotationLastMile = (params: QuotationLastMileSaveRequest) => {
  return request.post<boolean>('/price/quotation/last-mile/save', params)
}

/**
 * 修改尾程报价
 */
export const updateQuotationLastMile = (params: QuotationLastMileSaveRequest) => {
  return request.post<boolean>('/price/quotation/last-mile/update', params)
}

/**
 * 批量删除尾程报价
 */
export const batchDeleteQuotationLastMile = (params: BatchDeleteRequest) => {
  return request.post<boolean>('/price/quotation/last-mile/batch-delete', params)
}

/**
 * 发起新磋商
 * 针对指定的整箱报价单发起新的磋商记录,更新报价单磋商状态为"磋商中"
 */
export const initiateConsultationLastMile = (params: InitiateConsultationRequest) => {
  return request.post<boolean>('/price/quotation/last-mile/initiateConsultation', params)
}

/**
 * 商户报价单加价保存
 */
export const businessQuotationMarkupLastMile = (params: InqQuotPriceChargingItemVO) => {
  return request.post<InqQuotPriceChargingItemVO>('/price/quotation/last-mile/businessQuotationMarkup', params)
}

/**
 * 供应商报价单加价保存
 */
export const supplierQuotationMarkupLastMile = (params: QuotationChargingItemVO) => {
  return request.post<QuotationChargingItemVO>('/price/quotation/last-mile/supplierQuotationMarkup', params)
}

// ---- 匿名报价接口 ----

export interface LinkAccess {
  token: string
}

/** 尾程询价货物（InquiryLastMileCargoVO） */
export interface InquiryLastMileCargoVO {
  id?: string
  inquiryId?: string
  /** 箱型（FCL） */
  containerType?: string
  /** 箱量（FCL） */
  containerNum?: number
  /** 件数（LCL/TRUCK） */
  quantity?: number
  /** 毛重 */
  weight?: number
  length?: number
  width?: number
  height?: number
  /** 温度范围-起始（冷冻货） */
  temperatureStart?: number
  /** 温度范围-结束（冷冻货） */
  temperatureEnd?: number
  /** 通风开关 */
  ventilationEnable?: boolean
  /** 通风量 */
  ventilationQuantity?: number
  /** 湿度 */
  humidity?: number
  /** 是否预冷 */
  needPrecooling?: boolean
  /** 包装方式（LCL） */
  packageId?: number
  /** 整车类型（TRUCK）：1=敞车 2=棚车 3=平车 4=冷藏车 5=矿石车 6=水泥车 */
  truckType?: number
  remarks?: string
  [key: string]: unknown
}

/** 尾程询价基础信息（InquiryLastMileListVO + InquiryBaseVO 关键字段） */
export interface InquiryLastMileListVO {
  id?: string
  inquiryNo?: string
  status?: number
  /** 服务类型：FCL/LCL/TRUCK */
  serviceType?: string
  /** 运输方式：1=拖车 2=驳船 3=铁路 */
  lastMileTransportMode?: number
  /** 卸货地址类型：1=海港 2=铁路站点 3=机场 */
  dischargingAddressType?: number
  dischargingCountryId?: string
  dischargingCityId?: string
  /** 卸货港（InquiryBaseVO） */
  dischargingPortId?: string
  /** 卸货码头（InquiryBaseVO.deliveryTerminalId） */
  deliveryTerminalId?: string
  /** 卸货站 */
  dischargingStationId?: string
  /** 卸货机场 */
  dischargingAirportId?: string
  /** 机场货站 */
  airportTerminal?: string
  destinationStationId?: string
  destinationCountryId?: string
  destinationCityId?: string
  /** 驳船目的港（InquiryBaseVO） */
  destinationPortId?: string
  hsCode?: string
  onePriceSupported?: boolean
  quotationDeadline?: string
  specialRequirements?: string
  // InquiryBaseVO 配送地址字段
  /** 配送地址类型：1=第三方 2=亚马逊 3=易仓 */
  deliveryAddressType?: number
  postalCode?: string
  deliveryCountryId?: string
  deliveryProvinceId?: string
  deliveryCityId?: string
  deliveryDistrictId?: string
  deliveryAddress?: string
  deliveryStreetId?: string
  deliveryVillageId?: string
  warehouseId?: string
  // InquiryListVO 公共字段
  palletNature?: number
  paymentMethod?: string
  serviceTerm?: string
  transportClause?: string
  cargoReadyTimeType?: string
  cargoReadyTimeRange?: number
  cargoReadyTimeDate?: string
  etd?: string
  cargoType?: number
  unNo?: number
  hazardClass?: number
  cargoDescription?: string
  cargoValue?: number
  cargoValueCurrency?: string
  cargoList?: InquiryLastMileCargoVO[]
  [key: string]: unknown
}

/** 尾程报价费用条目（QuotationLastMileChargingItemVO） */
export interface QuotationLastMileChargingItemVO {
  id?: string
  quotationId?: string
  chargeItemId?: string | number
  /** 费用类型：0=基础费用 1=附加费 2=潜在费用 */
  feeType?: number
  /** 计费单位类型：1=按箱 2=非按箱 */
  billingUnitType?: number
  /** 箱型价格 JSON（按箱时使用）：{ "20GP": 150, "40HQ": 260 } */
  eqPrice?: Record<string, number> | string
  /** 箱型卖价 JSON（询价人模式取此槽） */
  eqPriceSell?: Record<string, number> | string
  /** 单价（非按箱时使用） */
  unitRate?: number | string
  /** 单价卖价（询价人模式取此槽） */
  unitRateSell?: number | string
  /** 总价是否已包含 */
  isTotalIncluded?: boolean
  /** 是否计入总价 */
  isCountTotal?: boolean
  /** 最低收费 */
  minCharge?: number | string
  /** 最高收费 */
  maxCharge?: number | string
  /** 最低收费卖价 */
  minChargeSell?: number | string
  /** 最高收费卖价 */
  maxChargeSell?: number | string
  /** 总价 */
  totalPrice?: number | string
  /** 总价卖价 */
  totalPriceSell?: number | string
  /** 委托链路共享费用行标识 */
  linkId?: string
  measureId?: string
  currency?: string
  /** 支付方式：1=预付 2=到付 */
  paymentType?: number
  remarks?: string
  [key: string]: unknown
}

/** 尾程报价方案（QuotationLastMileListVO + QuotationBaseVO 关键字段） */
export interface QuotationLastMileListVO {
  id?: string
  quotationNo?: string
  status?: number
  inquiryId?: string
  inquiryNo?: string
  supplierId?: string
  supplierName?: string
  contactPhone?: string
  contactEmail?: string
  consultationStatus?: number
  remarks?: string
  onePrice?: boolean
  onePriceSupported?: boolean
  validFrom?: string
  validTo?: string
  /** 服务类型：FCL/LCL/TRUCK */
  serviceType?: string
  /** 运输方式：1=拖车 2=驳船 3=铁路 */
  lastMileTransportMode?: number
  /** 卸货地址类型：1=海港 2=铁路站点 3=机场 */
  dischargingAddressType?: number
  dischargingCountryId?: string
  dischargingCityId?: string
  /** 卸货码头 */
  deliveryTerminalId?: string
  /** 卸货站 */
  dischargingStationId?: string
  /** 卸货机场 */
  dischargingAirportId?: string
  /** 机场货站 */
  airportTerminal?: string
  destinationStationId?: string
  destinationCountryId?: string
  destinationCityId?: string
  /** 驳船船名 */
  vessel?: string
  /** 驳船航次 */
  voyage?: string
  /** 驳船开航时间 */
  bargeEtd?: string
  /** 驳船到港时间 */
  bargeEta?: string
  /** 铁路公司 */
  railCompanyId?: string
  /** 线路分类 */
  railLineType?: string
  /** 车次 */
  railTrainNo?: string
  /** 发车时间 */
  railDepartureTime?: string
  // QuotationBaseVO 字段
  /** 卸货港 */
  dischargingPortId?: string
  /** 驳船目的港 */
  destinationPortId?: string
  /** 驳船航程（天） */
  transportationDays?: number
  /** 配送地址类型：1=第三方 2=亚马逊 3=易仓 */
  deliveryAddressType?: number
  postalCode?: string
  deliveryCountryId?: string
  deliveryProvinceId?: string
  deliveryCityId?: string
  deliveryDistrictId?: string
  deliveryAddress?: string
  warehouseId?: string
  chargingItemList?: QuotationLastMileChargingItemVO[]
  files?: Array<{ id?: string; name?: string; fileName?: string; url?: string; [key: string]: unknown }>
  [key: string]: unknown
}

/** 尾程匿名报价详情（含询价信息） */
export interface QuotationLastMileAnonymousDetailVO {
  quotations?: QuotationLastMileListVO[]
  inquiry?: InquiryLastMileListVO
}

/**
 * 通过 token 查询邮件授权范围内的尾程报价方案列表（含询价信息）
 */
export const queryByInquiryLastMileAnonymous = (access: LinkAccess) =>
  request.post<QuotationLastMileAnonymousDetailVO>('/price/quotation/last-mile/anonymous/queryByInquiry', { token: access.token })

export const createAnonymousLastMileScheme = (quotation: Record<string, unknown>, access: LinkAccess) =>
  request.post<number>('/price/quotation/last-mile/anonymous/createScheme', { quotation, token: access.token })

export const updateAnonymousLastMile = (quotation: Record<string, unknown>, access: LinkAccess) =>
  request.post<boolean>('/price/quotation/last-mile/anonymous/update', { quotation, token: access.token })

export const batchSubmitAnonymousLastMile = (quotations: Record<string, unknown>[], access: LinkAccess) =>
  request.post<boolean>('/price/quotation/last-mile/anonymous/batchSubmit', { quotations, token: access.token })

export const batchDeleteAnonymousLastMile = (ids: (string | number)[], access: LinkAccess) =>
  request.post<boolean>('/price/quotation/last-mile/anonymous/batchDelete', { ids, token: access.token })
